import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Moon, Loader2 } from "lucide-react";

const TOGGLES = [
  {
    key: "entry_created",
    label: "Neue Einträge",
    description: "Wenn dein Partner einen Wunsch oder ein Erlebnis hinzufügt.",
  },
  {
    key: "status_change",
    label: "Status-Wechsel",
    description: 'Wenn ein Eintrag als „erlebt" markiert oder geplant wird.',
  },
  {
    key: "reactions_comments",
    label: "Reaktionen & Kommentare",
    description: "Wenn jemand auf einen Eintrag reagiert oder kommentiert.",
  },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function fmtHour(h) {
  return String(h).padStart(2, "0") + ":00";
}

export default function NotificationPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/user/notification-prefs");
        setPrefs(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    })();
  }, []);

  const update = (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      // Capture current browser tz offset (minutes east of UTC)
      const tz = -new Date().getTimezoneOffset();
      const body = { ...prefs, quiet_hours_tz_offset_minutes: tz };
      const { data } = await api.put("/user/notification-prefs", body);
      setPrefs(data);
      setDirty(false);
      toast.success("Einstellungen gespeichert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return (
      <div className="bg-white border border-border rounded-3xl p-8 lg:col-span-2 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-text_secondary" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-3xl p-8 lg:col-span-2" data-testid="notification-prefs-card">
      <h2 className="font-serif text-2xl text-ink mb-1">Benachrichtigungs-Einstellungen</h2>
      <p className="text-text_secondary text-sm mb-6">Entscheide, wofür du Pushes bekommen möchtest und wann nicht gestört werden soll.</p>

      <div className="space-y-5">
        {TOGGLES.map(({ key, label, description }) => (
          <label
            key={key}
            htmlFor={`toggle-${key}`}
            className="flex items-start justify-between gap-6 cursor-pointer group"
            data-testid={`pref-${key}`}
          >
            <div className="flex-1">
              <div className="text-ink font-medium text-sm">{label}</div>
              <div className="text-text_secondary text-xs mt-0.5">{description}</div>
            </div>
            <Switch
              id={`toggle-${key}`}
              checked={prefs[key]}
              onCheckedChange={(v) => update({ [key]: v })}
              data-testid={`pref-${key}-switch`}
              className="mt-1"
            />
          </label>
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-border">
        <label className="flex items-start justify-between gap-6 cursor-pointer" data-testid="pref-quiet-hours">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-ink font-medium text-sm">
              <Moon size={14} /> Stille Stunden
            </div>
            <div className="text-text_secondary text-xs mt-0.5">In diesem Zeitraum werden keine Pushes zugestellt.</div>
          </div>
          <Switch
            checked={prefs.quiet_hours_enabled}
            onCheckedChange={(v) => update({ quiet_hours_enabled: v })}
            data-testid="pref-quiet-hours-switch"
            className="mt-1"
          />
        </label>

        {prefs.quiet_hours_enabled && (
          <div className="mt-4 grid grid-cols-2 gap-4" data-testid="quiet-hours-range">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Von</label>
              <select
                value={prefs.quiet_hours_start}
                onChange={(e) => update({ quiet_hours_start: parseInt(e.target.value, 10) })}
                className="mt-1 w-full rounded-2xl border border-border bg-bone py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="quiet-hours-start"
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>{fmtHour(h)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Bis</label>
              <select
                value={prefs.quiet_hours_end}
                onChange={(e) => update({ quiet_hours_end: parseInt(e.target.value, 10) })}
                className="mt-1 w-full rounded-2xl border border-border bg-bone py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="quiet-hours-end"
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>{fmtHour(h)}</option>
                ))}
              </select>
            </div>
            <p className="col-span-2 text-[11px] text-text_secondary">
              {prefs.quiet_hours_start === prefs.quiet_hours_end
                ? "Hinweis: Start- und End-Zeit sind gleich — keine Stille."
                : prefs.quiet_hours_start < prefs.quiet_hours_end
                  ? `Stille von ${fmtHour(prefs.quiet_hours_start)} bis ${fmtHour(prefs.quiet_hours_end)} (deine Zeitzone).`
                  : `Stille von ${fmtHour(prefs.quiet_hours_start)} bis ${fmtHour(prefs.quiet_hours_end)} am nächsten Tag.`}
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-full bg-ink text-alabaster hover:bg-ink/90 disabled:opacity-50"
          data-testid="save-notification-prefs"
        >
          {saving ? "Speichere…" : dirty ? "Speichern" : "Gespeichert"}
        </Button>
      </div>
    </div>
  );
}
